/**
 * SculptorSkillEffect — Earth tree sculptor path (Fork 1) active skill.
 *
 * Instant activation — places a 1×1 stone formation at short range (default
 * 3 tiles) that blocks movement and projectiles. Lasts 8 seconds. Max 2
 * active formations; placing a third evicts the oldest (LRU). Starts with
 * low effectiveness (mastery/fizzle governed externally).
 *
 * Upgrade keys on scope 'sculptor':
 * - placementRangeTiles: additive (Extended Reach: +2)
 * - maxFormations: additive (Mason: +1)
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Sculptor Path — Sculptor
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Terrain config interface (mirrors TerrainConfig subset for formations)
// ---------------------------------------------------------------------------

export interface SculptorTerrainConfig {
  readonly type: 'formation';
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SculptorDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly placement: {
    /**
     * Given the maximum placement range in tiles, returns the resolved
     * placement position (clamped to range) and the single-tile footprint.
     */
    getFormationPlacement(rangeTiles: number): {
      position: { x: number; y: number };
      footprintTiles: Array<{ x: number; y: number }>;
    };
  };
  readonly terrain: {
    place(config: SculptorTerrainConfig): string;
    remove(terrainId: string): void;
  };
  readonly pathfinding: {
    markImpassable(positions: Array<{ x: number; y: number }>): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SculptorConfig {
  /** Base HP of the placed formation. */
  readonly baseHp: number;
  /** Base duration in ms before expiry. */
  readonly baseDurationMs: number;
  /** Cooldown in ms after each activation (medium tier). */
  readonly cooldownMs: number;
  /** Width of the formation in tiles. */
  readonly widthTiles: number;
  /** Height of the formation in tiles. */
  readonly heightTiles: number;
  /** Base max placement range in tiles. */
  readonly baseRangeTiles: number;
  /** Base max active formations before LRU eviction. */
  readonly baseMaxActive: number;
}

const DEFAULT_CONFIG: SculptorConfig = {
  baseHp: 50,
  baseDurationMs: 8000,
  cooldownMs: 800,         // medium cooldown tier
  widthTiles: 1,
  heightTiles: 1,
  baseRangeTiles: 3,
  baseMaxActive: 2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SculptorSkillEffect extends InstantSkillEffect {
  readonly skillId = 'sculptor';

  private readonly deps: SculptorDeps;
  private readonly config: SculptorConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // Active formations in placement order (oldest first)
  private _activeFormations: string[] = [];

  // Telemetry
  private _formationsPlacedCount = 0;
  private _evictionCount = 0;
  private _pathfindingCallCount = 0;
  private _lastTerrainConfig: SculptorTerrainConfig | null = null;

  constructor(deps: SculptorDeps, config: Partial<SculptorConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }

  /** IDs of currently active formations, oldest first. */
  get activeFormations(): readonly string[] { return this._activeFormations; }

  get formationsPlacedCount(): number { return this._formationsPlacedCount; }

  get evictionCount(): number { return this._evictionCount; }

  get pathfindingCallCount(): number { return this._pathfindingCallCount; }

  /** The TerrainConfig used for the most recently placed formation. */
  get lastTerrainConfig(): SculptorTerrainConfig | null { return this._lastTerrainConfig; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // Resolve max active count (base + Mason's delta)
    const bonusMax = this.deps.resolver.getAdditiveEffect('sculptor', 'maxFormations');
    const maxActive = this.config.baseMaxActive + bonusMax;

    // LRU eviction: remove oldest formations until we have room for one more
    while (this._activeFormations.length >= maxActive) {
      const oldest = this._activeFormations.shift()!;
      this.deps.terrain.remove(oldest);
      this._evictionCount++;
    }

    // Resolve placement range (base + Extended Reach delta)
    const bonusRange = this.deps.resolver.getAdditiveEffect('sculptor', 'placementRangeTiles');
    const rangeTiles = this.config.baseRangeTiles + bonusRange;

    // Get placement position from game layer
    const { position, footprintTiles } = this.deps.placement.getFormationPlacement(rangeTiles);

    const terrainConfig: SculptorTerrainConfig = {
      type: 'formation',
      position,
      widthTiles: this.config.widthTiles,
      heightTiles: this.config.heightTiles,
      hp: this.config.baseHp,
      durationMs: this.config.baseDurationMs,
      blocksMovement: true,
      blocksProjectiles: true,
      sourceSkillId: 'sculptor',
    };

    const terrainId = this.deps.terrain.place(terrainConfig);
    this._activeFormations.push(terrainId);
    this._lastTerrainConfig = terrainConfig;
    this._formationsPlacedCount++;

    // Synchronous pathfinding update
    this.deps.pathfinding.markImpassable(footprintTiles);
    this._pathfindingCallCount++;

    // Start cooldown
    this._cooldownTimer = this.config.cooldownMs;

    return true;
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }
}
